import React, { useState, useEffect } from 'react';
import { oauthAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Link2, Unlink, Loader2, AlertTriangle } from 'lucide-react';

// Google icon SVG component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Microsoft icon SVG component
const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24">
    <path fill="#F25022" d="M1 1h10v10H1z"/>
    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
    <path fill="#FFB900" d="M13 13h10v10H13z"/>
  </svg>
);

function ConnectedAccounts() {
  const { success: showSuccess, error: showError } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [accountsRes, providersRes] = await Promise.all([
        oauthAPI.getAccounts(),
        oauthAPI.getProviders()
      ]);
      setAccounts(accountsRes.data.accounts || []);
      setProviders(providersRes.data.providers || []);
    } catch (error) {
      console.error('Failed to load connected accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    setActionLoading(provider);
    try {
      const response = await oauthAPI.getAuthUrl(provider, true);
      const { authUrl, state } = response.data;

      // Store state for CSRF protection
      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_provider', provider);
      sessionStorage.setItem('oauth_link_account', 'true');

      // Redirect to OAuth provider
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to connect:', error);
      showError(error.response?.data?.error || `Failed to connect ${provider}`);
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Are you sure you want to disconnect your ${provider} account?`)) {
      return;
    }

    setActionLoading(provider);
    try {
      await oauthAPI.unlinkAccount(provider);
      setAccounts(accounts.filter(a => a.provider !== provider));
      showSuccess(`${provider} account disconnected`);
    } catch (error) {
      console.error('Failed to disconnect:', error);
      showError(error.response?.data?.error || `Failed to disconnect ${provider}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return <GoogleIcon />;
      case 'microsoft':
        return <MicrosoftIcon />;
      default:
        return <Link2 size={24} />;
    }
  };

  const getProviderName = (provider) => {
    switch (provider) {
      case 'google':
        return 'Google';
      case 'microsoft':
        return 'Microsoft';
      default:
        return provider;
    }
  };

  const isConnected = (provider) => {
    return accounts.some(a => a.provider === provider);
  };

  const getConnectedAccount = (provider) => {
    return accounts.find(a => a.provider === provider);
  };

  if (loading) {
    return (
      <div className="settings-group">
        <h3>Connected Accounts</h3>
        <div className="loading-state">
          <Loader2 size={24} className="spinner" />
          <span>Loading connected accounts...</span>
        </div>
      </div>
    );
  }

  // If no providers are configured, don't show this section
  if (providers.length === 0) {
    return null;
  }

  return (
    <div className="settings-group">
      <h3>Connected Accounts</h3>
      <p className="setting-description">
        Link your social accounts for easier sign-in
      </p>

      <div className="connected-accounts-list">
        {providers.map(provider => {
          const connected = isConnected(provider);
          const account = getConnectedAccount(provider);
          const isLoading = actionLoading === provider;

          return (
            <div key={provider} className={`connected-account-item ${connected ? 'connected' : ''}`}>
              <div className="account-info">
                <div className="account-icon">
                  {getProviderIcon(provider)}
                </div>
                <div className="account-details">
                  <h4>{getProviderName(provider)}</h4>
                  {connected && account?.email ? (
                    <p className="account-email">{account.email}</p>
                  ) : (
                    <p className="account-status">Not connected</p>
                  )}
                </div>
              </div>
              
              <div className="account-actions">
                {connected ? (
                  <button
                    className="btn-danger-outline btn-sm"
                    onClick={() => handleDisconnect(provider)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 size={14} className="spinner" />
                    ) : (
                      <Unlink size={14} />
                    )}
                    Disconnect
                  </button>
                ) : (
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() => handleConnect(provider)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 size={14} className="spinner" />
                    ) : (
                      <Link2 size={14} />
                    )}
                    Connect
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {accounts.length > 0 && (
        <div className="connected-accounts-note">
          <AlertTriangle size={14} />
          <span>
            You can use connected accounts to sign in. Make sure you have a password set before disconnecting all accounts.
          </span>
        </div>
      )}
    </div>
  );
}

export default ConnectedAccounts;
